'use client';

import classNames from 'classnames';
import { HTMLAttributes, ReactNode } from 'react';

interface DetailsCardProps extends HTMLAttributes<HTMLDivElement> {
  icon: ReactNode;
  title: string;
  description?: string;
  editButton?: ReactNode;
  subheaderChildren?: ReactNode;
  bordered?: boolean;
  children: ReactNode;
}

const DetailsCard = ({
  icon,
  title,
  description,
  editButton,
  subheaderChildren,
  bordered = true,
  children,
  className,
  style,
  ...props
}: DetailsCardProps) => {
  return (
    <div style={style} className={classNames('w-full', className)} {...props}>
      {/* Header - no background, extends to edges */}
      <div className="flex items-end py-2">
        {/* Left content: Icon + Title + Description */}
        <div className="flex-1">
          <div className="flex flex-wrap items-baseline-last gap-x-4">
            {/* Icon + Title container - center aligned together */}
            <div className="text-neutral flex items-center dark:text-gray-400">
              <div className="flex justify-center">{icon}</div>
              <h3 className="ml-2 text-lg font-medium whitespace-nowrap">{title}</h3>
            </div>

            {/* Description - separate element that can wrap */}
            {description && <p className="text-sm text-gray-500">{description}</p>}
          </div>

          {/* Subheader - optional content between header and body */}
          {subheaderChildren && <div className="pt-1">{subheaderChildren}</div>}
        </div>

        {/* Edit button - bottom-aligned with text content */}
        {editButton && <div className="ml-4">{editButton}</div>}
      </div>

      {/* Body - outlined instead of gray background wrapper */}
      <div
        className={classNames({
          'dark:bg-base-100 rounded-2xl border border-gray-300 bg-white p-4 dark:border-gray-800':
            bordered,
        })}>
        {children}
      </div>
    </div>
  );
};

export default DetailsCard;
